import React from "react";
import SectionLabel from "./brand/SectionLabel";

// Map bounds: lat [48.871°N, 50.521°N], long [115.20°W, 110.50°W]
// Centered on Lethbridge (49.7°N, 112.8°W). View shifted: Lethbridge sits at (45%, 45%)
// to give a touch more room east and south on the map.

// Faded reference cities for geographic context (no pulse)
const REF_CITIES = [
  { city: "LETHBRIDGE", x: "45.1%", y: "45.0%" },
  { city: "MEDICINE HAT", x: "91.3%", y: "24.1%" },
  { city: "FORT MACLEOD", x: "33.1%", y: "43.3%" },
  { city: "PINCHER CREEK", x: "21.5%", y: "57.8%" },
  { city: "CARDSTON", x: "35.4%", y: "75.4%" },
  { city: "TABER", x: "59.9%", y: "39.5%" },
  { city: "WATERTON", x: "22.3%", y: "84.2%" },
];

// Active project sites — clean signal pings, no labels
const SITES = [
  { id: "p-barnwell", x: "57.2%", y: "45.8%" },
  { id: "p-burdett", x: "72.7%", y: "35.7%" },
  { id: "p-hillspring", x: "28.6%", y: "69.0%" },
  { id: "p-standoff", x: "39.0%", y: "53.8%" },
  { id: "p-bow-island", x: "76.5%", y: "34.5%" },
  { id: "p-magrath", x: "44.6%", y: "62.3%" },
  { id: "p-wrentham", x: "61.8%", y: "44.2%" },
  { id: "p-glenwood", x: "30.1%", y: "65.4%" },
  { id: "p-09", x: "31.1%", y: "86.7%" },
  { id: "p-10", x: "29.3%", y: "79.0%" },
  { id: "p-11", x: "22.5%", y: "84.0%" },
  { id: "p-12", x: "32.5%", y: "75.7%" },
  { id: "p-13", x: "35.4%", y: "48.5%" },
  { id: "p-redcliff", x: "88.9%", y: "21.7%" },
  { id: "p-pincher-n", x: "21.5%", y: "49.6%" },
  { id: "p-skiff", x: "70.2%", y: "60.2%" },
  { id: "p-foremost", x: "74.8%", y: "57.8%" },
  { id: "p-claresholm", x: "29.4%", y: "25.2%" },
  { id: "p-granum", x: "30.8%", y: "33.7%" },
  { id: "p-raymond", x: "49.1%", y: "60.0%" },
  { id: "p-coalhurst", x: "43.1%", y: "42.0%" },
  { id: "p-coaldale", x: "50.0%", y: "43.5%" },
  { id: "p-vulcan", x: "36.3%", y: "2.1%" },
  { id: "p-leth-n", x: "45.1%", y: "43.4%" },
  { id: "p-milk-river", x: "61.3%", y: "78.2%" },
  { id: "p-del-bonita", x: "46.5%", y: "86.7%" },
  { id: "p-aetna", x: "35.4%", y: "83.1%" },
  { id: "p-kimball", x: "41.6%", y: "86.6%" },
];

export default function ServiceArea() {
  return (
    <section
      id="coverage"
      data-testid="coverage-section"
      className="relative py-24 md:py-32 bg-bg-secondary border-t border-steel-dark"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <SectionLabel className="mb-5">48.9°N — 50.5°N</SectionLabel>
            <h2 className="heading text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95] mb-6">
              Southern <br />
              <span className="text-signal">Alberta</span>
              <br />
              Coverage.
            </h2>
            <p className="text-steel-light text-base md:text-lg leading-relaxed mb-6">
              Proudly serving customers across Southern Alberta, with a primary
              focus on the Cardston, Lethbridge, Magrath, and Fort Macleod
              regions — including rural communities, farms, acreages, and
              businesses throughout the surrounding areas. We regularly travel
              across Southern Alberta for specialized networking, wireless
              infrastructure, and communication projects.
            </p>
            <div className="border border-steel-dark bg-bg-primary/60 p-6">
              <div className="font-mono text-xs tracking-[0.2em] text-signal uppercase mb-2 flex items-center gap-3">
                <span className="inline-block w-6 h-[2px] bg-signal" />
                Travel Zones
              </div>
              <ul className="space-y-2 text-sm text-white/90 font-mono">
                <li className="flex justify-between border-b border-steel-dark pb-2">
                  <span>CORE</span>
                  <span className="text-steel-light">Southern AB</span>
                </li>
                <li className="flex justify-between border-b border-steel-dark pb-2">
                  <span>EXTENDED</span>
                  <span className="text-steel-light">AB, SK, BC</span>
                </li>
                <li className="flex justify-between">
                  <span>ON REQUEST</span>
                  <span className="text-steel-light">Canada-wide</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="md:col-span-7">
            <div
              data-testid="coverage-map"
              className="relative aspect-[3/2] border border-steel-dark bg-bg-primary overflow-hidden"
            >
              {/* Topo lines (SVG) */}
              <svg
                className="absolute inset-0 w-full h-full opacity-30"
                viewBox="0 0 400 300"
                preserveAspectRatio="none"
              >
                <defs>
                  <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
                    <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#2A2E33" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="400" height="300" fill="url(#grid)" />
                {/* contour lines */}
                {[...Array(6)].map((_, i) => (
                  <path
                    key={`contour-${i}`}
                    d={`M0,${40 + i * 40} Q100,${20 + i * 40} 200,${50 + i * 40} T400,${30 + i * 40}`}
                    fill="none"
                    stroke="#3F4448"
                    strokeWidth="0.5"
                  />
                ))}
                {/* Southern Alberta rough province outline suggestion */}
                <path
                  d="M30,10 L370,10 L370,290 L30,290 Z"
                  fill="none"
                  stroke="#E84A1A"
                  strokeOpacity="0.2"
                  strokeDasharray="4 4"
                />
              </svg>

              {/* reference cities (faded, no pulse) */}
              {REF_CITIES.map((c) => (
                <div
                  key={c.city}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: c.x, top: c.y }}
                  data-testid={`coverage-ref-${c.city.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-steel-light/40" />
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 font-mono text-[9px] tracking-[0.2em] text-steel-light/40 whitespace-nowrap">
                    {c.city}
                  </div>
                </div>
              ))}

              {/* active project sites — clean signal pings, no labels */}
              {SITES.map((n) => (
                <div
                  key={n.id}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: n.x, top: n.y }}
                  data-testid={`coverage-node-${n.id}`}
                >
                  <div className="relative">
                    <div className="w-3 h-3 bg-signal rounded-full animate-signal-pulse" />
                    <div className="absolute inset-0 w-3 h-3 bg-signal rounded-full blur-md" />
                  </div>
                </div>
              ))}

              {/* scan line */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div
                  className="absolute top-0 left-0 w-32 h-full opacity-20 animate-scan-line"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(232,74,26,0.6), transparent)",
                  }}
                />
              </div>

              {/* corner brackets */}
              {["top-0 left-0", "top-0 right-0", "bottom-0 left-0", "bottom-0 right-0"].map((pos) => (
                <div
                  key={pos}
                  className={`absolute ${pos} w-6 h-6 pointer-events-none`}
                >
                  <div
                    className={`absolute w-full h-[2px] bg-signal ${
                      pos.includes("top") ? "top-0" : "bottom-0"
                    }`}
                  />
                  <div
                    className={`absolute h-full w-[2px] bg-signal ${
                      pos.includes("left") ? "left-0" : "right-0"
                    }`}
                  />
                </div>
              ))}

              {/* legend */}
              <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-signal rounded-full" /> ACTIVE JOB SITES
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
